import React from 'react';
import { motion } from 'framer-motion';
import { Compass, Heart, Sparkles, Users } from 'lucide-react';
export function About() {
  const stats = [{
    label: 'Countries Visited',
    value: '60+'
  }, {
    label: 'Continents Explored',
    value: '6'
  }, {
    label: 'Passports Filled',
    value: '4'
  }, {
    label: 'Years of Adventure',
    value: '40'
  }];
  return <section className="py-32 bg-rich-black relative overflow-hidden" data-id="element-13">
      {/* Decorative Background */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gold/5 rounded-full blur-[100px] pointer-events-none" data-id="element-14" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-emerald/10 rounded-full blur-[100px] pointer-events-none" data-id="element-15" />

      <div className="container mx-auto px-6 relative z-10" data-id="element-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-17">
          {/* Image Side */}
          <motion.div initial={{
          opacity: 0,
          x: -50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 1
        }} className="relative" data-id="element-18">
            <div className="relative rounded-2xl overflow-hidden aspect-[4/5] border border-gold/20 bg-gradient-to-br from-emerald via-ocean to-fuchsia" data-id="element-19">
              {/* Layered glows for depth */}
              <div className="absolute -top-20 -left-20 w-[400px] h-[400px] bg-gold/40 rounded-full blur-[100px]" data-id="element-20" />
              <div className="absolute -bottom-20 -right-20 w-[400px] h-[400px] bg-sunset/40 rounded-full blur-[100px]" data-id="element-21" />
              <div className="absolute inset-0 bg-gradient-to-t from-rich-black/60 via-transparent to-transparent" data-id="element-22" />

              {/* Decorative passport-style frame ring */}
              <div className="absolute inset-4 border border-gold/20 rounded-xl pointer-events-none" data-id="element-23" />

              {/* Cutout subject */}
              <img src="https://cdn.magicpatterns.com/uploads/aygNuKkPpnVZexzq8b1rd8/Untitled_design_-_2026-05-08T210417.474.png" alt="Amoye Henry" className="relative w-full h-full object-contain object-bottom drop-shadow-[0_20px_40px_rgba(0,0,0,0.5)]" data-id="element-24" />
            </div>

            {/* Floating Badge */}
            <motion.div animate={{
            y: [-10, 10, -10]
          }} transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute -bottom-8 -right-8 glass-panel p-6 rounded-2xl max-w-[200px]" data-id="element-25">
              <div className="text-gold mb-2" data-id="element-26">
                <Compass size={32} data-id="element-27" />
              </div>
              <p className="font-display font-bold text-xl" data-id="element-28">Global Citizen</p>
              <p className="font-sans text-sm text-gray-400" data-id="element-29">Since Day One</p>
            </motion.div>
          </motion.div>

          {/* Text Side */}
          <motion.div initial={{
          opacity: 0,
          x: 50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 1
        }} data-id="element-30">
            <h2 className="font-display text-4xl md:text-5xl font-bold mb-6" data-id="element-31">
              Who is <span className="text-gradient-gold" data-id="element-32">Amoye?</span>
            </h2>

            <div className="space-y-6 font-sans text-lg text-gray-300 leading-relaxed" data-id="element-33">
              <p data-id="element-34">
                Entrepreneur. Investor. Visionary. Connector. I've spent my life
                building businesses, communities, and opportunities across
                continents. But above all, I believe life should be lived boldly
                and intentionally.
              </p>
              <p data-id="element-35">
                Having traveled to over 60 countries, I've learned that the most
                beautiful parts of life aren't things—they are the people we
                meet, the cultures we immerse ourselves in, and the impact we
                leave behind.
              </p>
              <p className="text-gold font-medium italic" data-id="element-36">
                "Now entering a new chapter at 40 with gratitude, joy, and an
                unquenchable thirst for adventure."
              </p>
            </div>

            {/* Traits */}
            <div className="grid grid-cols-2 gap-6 mt-10" data-id="element-37">
              <div className="flex items-center gap-3" data-id="element-38">
                <div className="p-3 rounded-full bg-emerald/20 text-emerald" data-id="element-39">
                  <Sparkles size={20} data-id="element-40" />
                </div>
                <span className="font-sans font-medium" data-id="element-41">Visionary Leader</span>
              </div>
              <div className="flex items-center gap-3" data-id="element-42">
                <div className="p-3 rounded-full bg-fuchsia/20 text-fuchsia" data-id="element-43">
                  <Heart size={20} data-id="element-44" />
                </div>
                <span className="font-sans font-medium" data-id="element-45">Culture Advocate</span>
              </div>
              <div className="flex items-center gap-3" data-id="element-46">
                <div className="p-3 rounded-full bg-ocean/20 text-ocean" data-id="element-47">
                  <Users size={20} data-id="element-48" />
                </div>
                <span className="font-sans font-medium" data-id="element-49">Community Builder</span>
              </div>
              <div className="flex items-center gap-3" data-id="element-50">
                <div className="p-3 rounded-full bg-sunset/20 text-sunset" data-id="element-51">
                  <Compass size={20} data-id="element-52" />
                </div>
                <span className="font-sans font-medium" data-id="element-53">
                  Relentless Explorer
                </span>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12 pt-12 border-t border-white/10" data-id="element-54">
              {stats.map((stat, index) => <div key={index} className="text-center md:text-left" data-id="element-55">
                  <div className="font-display text-3xl font-bold text-white mb-1" data-id="element-56">
                    {stat.value}
                  </div>
                  <div className="font-sans text-xs text-gray-400 uppercase tracking-wider" data-id="element-57">
                    {stat.label}
                  </div>
                </div>)}
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}